import { useTranslation } from 'react-i18next'
import {
  Box,
  HStack,
  IconButton,
  Popover,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react'
import { Building2, ShieldCheck, UserRound } from 'lucide-react'
import { useAuthStore } from '@/shared/store/authStore'

/** Only the Blood Service roles mean anything here — SSO-wide ones are left out. */
const BLOOD_ROLE_PREFIX = 'BLOOD_'

export function UserRolesPopover() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)

  const roles = (user?.roles ?? []).filter((role) =>
    role.startsWith(BLOOD_ROLE_PREFIX),
  )
  const organization = user?.ctx?.at(0)?.organization_name

  return (
    <Popover.Root positioning={{ placement: 'bottom-end' }}>
      <Popover.Trigger asChild>
        <IconButton
          variant="ghost"
          size="sm"
          borderRadius="full"
          color="fg.muted"
          aria-label={t('user_menu.label')}
        >
          <UserRound size={18} />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="300px" borderRadius="xl">
            <Popover.Body p={4}>
              <Stack gap={3}>
                {organization && (
                  <HStack gap={2} align="flex-start">
                    <Box color="primary.fg" mt="2px" flexShrink={0}>
                      <Building2 size={16} />
                    </Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      {organization}
                    </Text>
                  </HStack>
                )}

                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="fg.muted"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {t('user_menu.roles')}
                </Text>

                {roles.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">
                    {t('user_menu.no_roles')}
                  </Text>
                ) : (
                  <Stack gap={1.5}>
                    {roles.map((role) => (
                      <HStack
                        key={role}
                        gap={2}
                        px={3}
                        py={2}
                        borderRadius="md"
                        bg="primary.subtle"
                      >
                        <Box color="primary.fg" flexShrink={0}>
                          <ShieldCheck size={16} />
                        </Box>
                        <Text fontSize="sm" fontWeight="medium">
                          {/* Unknown codes fall back to the raw role name. */}
                          {t(`roles.${role}`, { defaultValue: role })}
                        </Text>
                      </HStack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}
